import React, { useState } from 'react';
import dayjs from 'dayjs';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AttachmentThumb from '../../components/patient-attachments/AttachmentThumb';
import { clearAttachmentFileCache } from '../../components/patient-attachments/AuthFile';
import { DataProviderInstance } from '../../api/DataProvider';
import {
  EVOLUTION_PHOTO_ANGLES,
  EVOLUTION_PHOTO_REGIONS,
  buildPhotoFields,
  getPhotoAngle,
  getPhotoRegion,
  photoLabel,
} from '../../pages/appointment/modules/evolutionPhotos';
import styles from './EvolutionPhotoEditPopup.module.css';

const MAX_NOTE_LENGTH = 255;

/**
 * Edição de uma foto de evolução: data, região, ângulo e nota, com exclusão.
 * Trabalha em rascunho; "Salvar" grava pelo update.php dos anexos e
 * "Remover" pede confirmação antes do delete.php. `OnHide(changed)`.
 */
function EvolutionPhotoEditPopup({ photo, OnHide }) {
  const { UpdatePatientAttachment, DeletePatientAttachment } = DataProviderInstance();

  const [draft, setDraft] = useState({
    date: photo.documentDate ? dayjs(photo.documentDate) : null,
    region: getPhotoRegion(photo),
    angle: getPhotoAngle(photo),
    note: photo.note || '',
  });
  const [saving, setSaving] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);

  const update = (patch) => setDraft((current) => ({ ...current, ...patch }));

  function saveHandler() {
    setSaving(true);
    const fields = {
      id: photo.id,
      documentDate: draft.date && draft.date.isValid() ? draft.date.format('YYYY-MM-DD') : photo.documentDate,
      ...buildPhotoFields({ region: draft.region, angle: draft.angle, note: draft.note }),
    };
    UpdatePatientAttachment(fields, (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') OnHide(true);
    });
  }

  function removeHandler() {
    setShowConfirmRemove(false);
    DeletePatientAttachment(photo.id, (response) => {
      if (response && response.status === 'SUCCESS') {
        clearAttachmentFileCache(photo.id);
        OnHide(true);
      }
    });
  }

  const content = (
    <div className={styles.container}>
      <div className={styles.preview}>
        <AttachmentThumb attachment={photo} size={72} />
        <div className={styles.previewText}>
          <span className={styles.previewTitle}>{photoLabel(photo)}</span>
          <span className={styles.previewMeta}>
            {photo.documentDate ? dayjs(photo.documentDate).format('DD/MM/YYYY') : 'Sem data'}
          </span>
        </div>
      </div>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="Data da foto"
          value={draft.date}
          format="DD/MM/YYYY"
          disableFuture={true}
          onChange={(value) => update({ date: value })}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
      </LocalizationProvider>

      <div className={styles.row}>
        <FormControl size="small" fullWidth>
          <InputLabel id="evolution-photo-popup-region">Região</InputLabel>
          <Select
            labelId="evolution-photo-popup-region"
            label="Região"
            value={draft.region}
            onChange={(event) => update({ region: event.target.value })}
          >
            <MenuItem value="">Sem região</MenuItem>
            {EVOLUTION_PHOTO_REGIONS.map((item) => (
              <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel id="evolution-photo-popup-angle">Ângulo</InputLabel>
          <Select
            labelId="evolution-photo-popup-angle"
            label="Ângulo"
            value={draft.angle}
            onChange={(event) => update({ angle: event.target.value })}
          >
            <MenuItem value="">Sem ângulo</MenuItem>
            {EVOLUTION_PHOTO_ANGLES.map((item) => (
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <TextField
        size="small"
        fullWidth
        multiline
        minRows={2}
        label="Nota"
        value={draft.note}
        inputProps={{ maxLength: MAX_NOTE_LENGTH }}
        onChange={(event) => update({ note: event.target.value })}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <CustomButton
        variant="outlined"
        style="error"
        label="Remover"
        icon={<IconDelete />}
        onClick={() => setShowConfirmRemove(true)}
      />
      <div className={styles.footerRight}>
        <CustomButton variant="outlined" style="secondary" label="Cancelar" onClick={() => OnHide(false)} />
        <CustomButton
          variant="contained"
          style="primary"
          label="Salvar"
          icon={<IconSave />}
          loading={saving}
          onClick={saveHandler}
        />
      </div>

      {showConfirmRemove && (
        <ConfirmPopup
          Visibility={showConfirmRemove}
          Title="Remover foto"
          Description={`Remover a foto ${photoLabel(photo)} deste paciente? Esta ação não pode ser desfeita.`}
          ConfirmLabel="Remover"
          OnConfirm={removeHandler}
          OnHide={() => setShowConfirmRemove(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title="Editar foto de evolução"
      Content={content}
      Footer={footer}
      OnClose={() => OnHide(false)}
      maxWidth="sm"
    />
  );
}

export default EvolutionPhotoEditPopup;
